import { useEffect, useMemo, useState } from "react";

import useViemPublicClient from "@/hooks/viem/useViemPublicClient";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { MaxAllowanceTransferAmount, PERMIT2_ADDRESS, PermitTransferFrom, SignatureTransfer } from "@uniswap/permit2-sdk";
import { type Address, type Chain, encodeAbiParameters, type GetBlockReturnType, type TypedDataDomain } from "viem";
import { useSignTypedData } from "wagmi";
import { usePermit2Allowance } from "./usePermit2Allowance";

export enum ApprovalState {
  UNKNOWN = "UNKNOWN",
  NOT_APPROVED = "NOT_APPROVED",
  PENDING = "PENDING",
  APPROVED = "APPROVED",
}

function toDeadline(expiration: number): number {
  return Math.floor((Date.now() + expiration) / 1000);
}

export interface Permit2Actions {
  error: Error | null;
  state: ApprovalState;
  amount: bigint;
  signArgs: any;
  sign?: (params: any) => void;
  deadline: number | undefined;
  nonce: number | undefined;
  signature?: Address | undefined;
  enable?: boolean;
  encodedSignature?: Address | undefined;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

export default function usePermit2ApproveCallback(
  watch: boolean,
  token: Address,
  nonceIncrement: number,
  amount?: bigint,
  spender?: Address | undefined,
  enable = false
): Permit2Actions {
  const { network: chain } = useDynamicContext();
  const publicClient = useViemPublicClient();
  const userAddress = useWeb3UserAddress();
  const currentAllowance = usePermit2Allowance(watch, token, spender, enable);

  /// Give max approval to permit2 contract for this token (if user not already max approved)

  const amountToApprove = useMemo(() => {
    return amount ? amount : MaxAllowanceTransferAmount.toBigInt();
  }, [amount]);

  const [currentBlock, setCurrentBlock] = useState<GetBlockReturnType<Chain> | undefined>(undefined);
  const [nonce, setNonce] = useState<number | undefined>(undefined);

  useEffect(() => {
    void publicClient?.getBlock().then(block => {
      if (!currentBlock) {
        setCurrentBlock(block);
      }
    });
    const nonceBase = Date.now();
    void publicClient?.getTransactionCount({ address: userAddress as Address }).then(clientNonce => {
      if (nonce) return;
      setNonce(clientNonce + nonceBase + nonceIncrement);
    });
  }, [publicClient, userAddress, currentBlock, nonce, nonceIncrement]);

  const deadline = useMemo(() => {
    if (!currentBlock) return undefined;
    return Number(currentBlock.timestamp) + 60 * 60;
  }, [currentBlock]);

  const permitData = useMemo(() => {
    if (!deadline || !nonce || !spender || !chain || !enable) return undefined;

    const permitSingle: PermitTransferFrom = {
      permitted: {
        token: token,
        amount: amountToApprove,
      },
      spender: spender,
      nonce: nonce,
      deadline: deadline,
    };

    try {
      return SignatureTransfer.getPermitData(permitSingle, PERMIT2_ADDRESS, chain as number);
    } catch (e) {
      console.log(e);
      return undefined;
    }
  }, [chain, spender, token, nonce, deadline, amountToApprove, enable]);

  const signArgs = useMemo(
    () => ({
      domain: permitData?.domain as TypedDataDomain,
      primaryType: "PermitTransferFrom",
      message: permitData?.values as any,
      types: permitData?.types as Record<string, any>,
    }),
    [permitData]
  );

  const { data: signature, signTypedData, isPending: isLoading, isError, isSuccess, error } = useSignTypedData();

  const encodedSignature = useMemo(() => {
    if (!signature || !nonce || !deadline) {
      return undefined;
    }
    return encodeAbiParameters(
      [
        { name: "nonce", type: "uint256" },
        { name: "deadline", type: "uint256" },
        { name: "signature", type: "bytes" },
      ],
      [BigInt(nonce), BigInt(deadline), signature]
    );
  }, [nonce, deadline, signature]);

  const approvalState: ApprovalState = useMemo(() => {
    if (!spender) return ApprovalState.UNKNOWN;
    // if (isNative)
    //   return ApprovalState.APPROVED
    if (isLoading) return ApprovalState.PENDING;
    if (!currentAllowance) return ApprovalState.UNKNOWN;

    // when we got the signature, that means we have already approved
    if (signature !== undefined) return ApprovalState.APPROVED;

    // amountToApprove will be defined if currentAllowance is
    return currentAllowance.amount.lt(amountToApprove.toString()) || currentAllowance.expiration > toDeadline(0) ? ApprovalState.NOT_APPROVED : ApprovalState.APPROVED;
  }, [currentAllowance, amountToApprove, isLoading, signature, spender]);

  return useMemo(
    () => ({
      state: approvalState,
      amount: amountToApprove,
      signArgs,
      sign: signTypedData,
      deadline,
      nonce,
      signature,
      encodedSignature,
      isLoading,
      isError,
      error,
      isSuccess,
    }),
    [approvalState, amountToApprove, signArgs, signTypedData, deadline, nonce, signature, encodedSignature, isLoading, isError, error, isSuccess]
  );
}
